import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api';
import './SupportPage.css';

interface SupportPageProps {
  onClose: () => void;
}

const SupportPage: React.FC<SupportPageProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      // Mirror executePlatform pattern: plain JSON string, backend reads with @RequestParam + ObjectMapper
      formData.append('request', JSON.stringify({
        name: form.name,
        phone: form.phone,
        email: form.email,
        subject: form.subject,
        body: form.message,
      }));

      if (files.length > 0) {
        files.forEach(f => formData.append('files', f));
      } else {
        formData.append('files', new Blob([]), '');
      }

      await apiClient.post(API_ENDPOINTS.PLATFORM.CUSTOMER_SUPPORT, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSuccess(true);
    } catch {
      setError(t('support.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="support-page">
      <div className="support-header">
        <h2 className="support-title">{t('support.title')}</h2>
      </div>

      {success ? (
        <div className="support-success">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <p>{t('support.successMessage')}</p>
          <p className="support-success-sub">{t('support.successSub')}</p>
          <button className="support-submit-btn" onClick={onClose}>{t('support.close')}</button>
        </div>
      ) : (
        <form className="support-form" onSubmit={handleSubmit}>
          <p className="support-intro">{t('support.intro')}</p>

          <div className="support-row">
            <div className="support-field">
              <label htmlFor="sup-name">{t('support.name')}</label>
              <input id="sup-name" name="name" type="text" value={form.name} onChange={handleChange} placeholder={t('support.namePlaceholder')} />
            </div>
            <div className="support-field">
              <label htmlFor="sup-phone">{t('support.phone')}</label>
              <input id="sup-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder={t('support.phonePlaceholder')} />
            </div>
          </div>

          <div className="support-field">
            <label htmlFor="sup-email">{t('support.email')}</label>
            <input id="sup-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder={t('support.emailPlaceholder')} />
          </div>

          <div className="support-field">
            <label htmlFor="sup-subject">
              {t('support.subject')} <span className="support-required">*</span>
            </label>
            <input id="sup-subject" name="subject" type="text" value={form.subject} onChange={handleChange} required placeholder={t('support.subjectPlaceholder')} />
          </div>

          <div className="support-field">
            <label htmlFor="sup-message">
              {t('support.message')} <span className="support-required">*</span>
            </label>
            <textarea id="sup-message" name="message" value={form.message} onChange={handleChange} required rows={5} placeholder={t('support.messagePlaceholder')} />
          </div>

          <div className="support-field">
            <label htmlFor="sup-files">{t('support.files')}</label>
            <label className="support-file-label" htmlFor="sup-files">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
              </svg>
              {files.length > 0 ? `${files.length} ${t('support.filesSelected')}` : t('support.filesButton')}
              <input id="sup-files" type="file" multiple onChange={handleFiles} />
            </label>
          </div>

          {error && <div className="support-error">{error}</div>}

          <button type="submit" className="support-submit-btn" disabled={isSubmitting}>
            {isSubmitting ? t('support.submitting') : t('support.submit')}
          </button>
        </form>
      )}
    </div>
  );
};

export default SupportPage;
